import React, { useState, useEffect } from 'react';
import TransactionFilters from '../components/TransactionFilters';
import useChargingSessionsByStation from '../hooks/useChargingSessionsByStation';
import { toast } from 'react-hot-toast';
import { CreditCard, ChevronUp, ChevronDown } from 'lucide-react';
import { exportToCSV } from '../utils/exportData';
import { formatINR } from "../utils/formatINR";



const ChargingSessionsMobile = ({ stationId }) => {
  const { sessions, isLoading, error, message } = useChargingSessionsByStation(stationId);
  const [localSessions, setLocalSessions] = useState([]);
  const [expandedCards, setExpandedCards] = useState(new Set());

    const handleExport = () => {
      exportToCSV(sessions, `charging_sessions_${stationId}`);
    };
    

  useEffect(() => {
    if (message) toast.success(message);
  }, [message]);

  useEffect(() => {
    if (sessions) {
      const cleaned = sessions.map(s => ({
        ...s,
        user_id: s.user_id?.Valid ? s.user_id.String : "",
      }));
      setLocalSessions(cleaned);
    }
  }, [sessions]);

  const [filters, setFilters] = useState({
    search: '',
    status: 'all',
    dateRange: '30',
    startDate: null,
    endDate: null
  });

  const handleFilterChange = (name, value) => setFilters(prev => ({ ...prev, [name]: value }));
  const handleClearFilters = () => setFilters({ search: '', status: 'all', dateRange: '30', startDate: null, endDate: null });

  const filteredSessions = localSessions.filter(session => {
    if (filters.search && !session.id.toLowerCase().includes(filters.search.toLowerCase())) return false;

    if (filters.status !== 'all') {
      const isCompleted = session.session_end && session.total_cost >= 0;
      if (filters.status === 'Completed' && !isCompleted) return false;
      if (filters.status === 'Ongoing' && isCompleted) return false;
    }

    const sessionDate = new Date(session.session_start);
    const today = new Date();
    if (filters.dateRange === 'today') {
      const isToday =
        sessionDate.getDate() === today.getDate() &&
        sessionDate.getMonth() === today.getMonth() &&
        sessionDate.getFullYear() === today.getFullYear();
      if (!isToday) return false;
    } else if (filters.dateRange === 'custom') {
      if (filters.startDate && sessionDate < new Date(filters.startDate)) return false;
      if (filters.endDate && sessionDate > new Date(filters.endDate)) return false;
    } else if (filters.dateRange !== 'all') {
      const days = parseInt(filters.dateRange);
      const cutoff = new Date();
      cutoff.setDate(cutoff.getDate() - days);
      if (sessionDate < cutoff) return false;
    }

    return true;
  });

  const formatDate = dateString => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString('en-IN', {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit', hour12: true
    });
  };

  const getStatusBadge = session => {
    const isCompleted = session.session_end && session.total_cost >= 0;
    return (
      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
        isCompleted ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
      }`}>
        {isCompleted ? 'Completed' : 'Ongoing'}
      </span>
    );
  };

  const toggleCardExpansion = idx => {
    const newExpanded = new Set(expandedCards);
    expandedCards.has(idx) ? newExpanded.delete(idx) : newExpanded.add(idx);
    setExpandedCards(newExpanded);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="px-4 py-4 flex flex-col space-y-2">
          <h1 className="text-xl font-bold text-gray-900">Charging Sessions</h1>
          <button
              onClick={handleExport}
              className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors text-sm font-medium"
            >
              Export Data
            </button>
        </div>
      </header>

      <main className="px-4 py-4">
        <TransactionFilters filters={filters} onFilterChange={handleFilterChange} onClearFilters={handleClearFilters} />

        {/* Station info */}
        <div className="mt-4 mb-4">
          <h2 className="text-lg font-semibold text-gray-900">Station {stationId} Sessions</h2>
          <p className="text-sm text-gray-500 mt-1">{filteredSessions.length} session{filteredSessions.length !== 1 ? 's' : ''} found</p>
        </div>

        {/* Loading */}
        {/* {isLoading && <div className="py-12 text-center text-gray-600">Loading sessions...</div>} */}

        {/* Error */}
        {/* {error && <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center text-red-600">{error}</div>} */}

        {/* Empty */}
        {!isLoading && !error && filteredSessions.length === 0 && (
          <div className="bg-white rounded-lg border p-8 text-center">
            <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
              <CreditCard className="w-8 h-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No sessions found</h3>
            <p className="text-gray-500 text-sm">Try adjusting your filters to see more results.</p>
          </div>
        )}

        {/* Session Cards */}
        <div className="space-y-4">
          {filteredSessions.map((session, idx) => (
            <div key={idx} className="bg-white rounded-lg border shadow-sm overflow-hidden">
              {/* Card Header */}
              <div className="bg-gray-50 px-4 py-3 border-b flex items-center justify-between">
                <div className="flex items-center gap-2 text-sm font-medium text-gray-900">
                  <div className="w-2 h-2 bg-green-600 rounded-full"></div>
                  Vehicle {session.vehicle_id || '-'}
                </div>
                {getStatusBadge(session)}
              </div>

              {/* Card Content */}
              <div className="p-4">
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div>
                    <p className="text-xs text-gray-500 uppercase tracking-wide font-medium">Energy Consumed</p>
                    <p className="text-lg font-semibold text-gray-900">{session.energy_kwh} kWh</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 uppercase tracking-wide font-medium">Final Cost</p>
                    <p className="text-lg font-semibold text-green-600">{formatINR(session.final_cost)}</p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div>
                    <p className="text-xs text-gray-500 uppercase tracking-wide font-medium">Gun ID</p>
                    <p className="text-sm text-gray-900">{session.gun_id}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 uppercase tracking-wide font-medium">Duration</p>
                    <p className="text-sm text-gray-900">{Math.round(session.duration_seconds / 60)} min</p>
                  </div>
                </div>

                {/* SOC */}
                <div className="mb-4">
                  <p className="text-xs text-gray-500 uppercase tracking-wide font-medium mb-2">State of Charge</p>
                  <div className="flex items-center space-x-2">
                    <div className="flex-1">
                      <div className="flex items-center justify-between text-xs text-gray-500 mb-1">
                        <span>Start: {session.start_soc}%</span>
                        <span>End: {session.end_soc}%</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div className="bg-green-600 h-2 rounded-full" style={{ width: `${session.end_soc}%` }}></div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Expandable Details */}
                <button
                  onClick={() => toggleCardExpansion(idx)}
                  className="w-full flex justify-between items-center py-2 text-sm font-medium border-t border-gray-200 mt-2 text-gray-700"
                >
                  {expandedCards.has(idx) ? "Hide Details" : "View Details"}
                  {expandedCards.has(idx) ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                </button>

                {expandedCards.has(idx) && (
                  <div className="mt-2 space-y-2 text-sm">
                    {[
                      { label: "Session ID", value: session.id },
                      { label: "Start Time", value: formatDate(session.session_start) },
                      { label: "End Time", value: session.session_end ? formatDate(session.session_end) : '-' },
                      { label: "User ID", value: session.user_id }
                    ].map(item => item.value && (
                      <div key={item.label} className="flex justify-between border-b py-1">
                        <span className="text-gray-500">{item.label}</span>
                        <span className="font-semibold text-gray-900">{item.value}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </main>
    </div>
  );
};

export default ChargingSessionsMobile;
